import { useState, useMemo, useEffect, useContext, useRef } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";
import usePosOrders from "../../../services/Pos/usePosOrders";
import PaymentModal from "./PaymentModel";
import { toast, ToastContainer } from "react-toastify";
import MobileNumberModal from "./MobileNumberModal";
import KotGeneratedModal from "./KotGeneratedModal";
import useDining from "../../../services/Order/useDining";

export default function DineScreen({
  table,
  orderType,
  orderId,
  mobile,
  existingItems = [],
  reciptDetails,
  catalogueCache,
  menuCacheVersion,
  refreshCatalogueCache,
  loadingMenu,
  onBack,
  onClose,
  onHoldSuccess,
}) {
  const normalizeOrderTypeForCatalog = (type) => {
    const normalized = String(type || "")
      .toLowerCase()
      .trim();

    if (normalized === "homedelivery") return "home_delivery";
    if (normalized === "home_delivery") return "home_delivery";

    if (normalized === "dinein") return "dining";
    if (normalized === "dine-in") return "dining";
    if (normalized === "dining") return "dining";

    if (normalized === "takeway") return "takeaway";
    if (normalized === "take away") return "takeaway";
    if (normalized === "takeaway") return "takeaway";

    // Room order will use dining catalogue/menu
    if (
      normalized === "room_order" ||
      normalized === "room order" ||
      normalized === "roomorder"
    ) {
      return "dining";
    }

    return "takeaway";
  };

  const normalizedOrderType = normalizeOrderTypeForCatalog(orderType);

  const getPayloadOrderType = () => {
    const rawType = String(orderType || "")
      .toLowerCase()
      .trim();

    if (
      rawType === "room_order" ||
      rawType === "room order" ||
      rawType === "roomorder"
    ) {
      return "room_order";
    }

    return normalizedOrderType;
  };

  const payloadOrderType = getPayloadOrderType();

  const isRoomOrder = payloadOrderType === "room_order";

  const getCurrentTableNo = () => {
    const type = normalizeOrderTypeForCatalog(orderType);

    if (type !== "dining" && !isRoomOrder) return "";

    if (typeof table === "object") {
      return (
        table?.tableNo ||
        table?.table_no ||
        table?.roomNumber ||
        table?.roomNo ||
        table?.room_no ||
        table?.table ||
        table?.name ||
        ""
      );
    }

    return table || "";
  };

  const normalizeMobile = (m) => (typeof m === "object" ? m.mobile : m);

  const { userID } = useContext(GlobalContext);

  const [activeCategory, setActiveCategory] = useState("");
  const [cart, setCart] = useState([]);
  const [searchItem, setSearchItem] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerMobile, setCustomerMobile] = useState(normalizeMobile(mobile));
  const [customerName, setCustomerName] = useState("");
  const [showCustomize, setShowCustomize] = useState(false);
  const [selectedCartIndex, setSelectedCartIndex] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});

  const [showKotModal, setShowKotModal] = useState(false);
  const [kotOrderData, setKotOrderData] = useState(null);
  const [kotOrderId, setKotOrderId] = useState(orderId || "");
  const [kotLoading, setKotLoading] = useState(false);
  const [kotCartSnapshot, setKotCartSnapshot] = useState([]);
const [activeOrderId, setActiveOrderId] = useState(orderId || "");

  const searchRef = useRef(null);

const {
  catalogueStore = [],
  getCatalog,
  catalogLoading,
  generateOrderID,
  placePosOrder,
  setCatalogueStore,
  getDiscountList,
  getOrderDetails,
  getOrders,
  printKot,
} = usePosOrders();

  const { refreshDiningCache } = useDining();

  const handleCancel = async () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0");
  const day = String(
    date.getDate(),
  ).padStart(2, "0");

  const currentDate = `${year}-${month}-${day}`;

  try {
    await getOrders({
      fromDate: currentDate,
      toDate: currentDate,
    });
  } catch (error) {
    console.error(
      "Get POS orders on cancel error:",
      error,
    );
  } finally {
    setCart([]);
    setActiveOrderId("");
    setSelectedCartIndex(null);
    setSelectedOptions({});
    setShowCustomize(false);

    onClose?.();
  }
};

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(catalogueStore) && catalogueStore.length > 0) {
      setActiveCategory(catalogueStore[0].category_name);
    } else {
      setActiveCategory("");
    }
  }, [catalogueStore]);

  useEffect(() => {
    if (!userID) return;

    const activeType = normalizeOrderTypeForCatalog(orderType);

    if (catalogueCache && Array.isArray(catalogueCache[activeType])) {
      setCatalogueStore(catalogueCache[activeType]);
      return;
    }

    getCatalog({ orderType: normalizedOrderType, force: false });
  }, [
    userID,
    orderType,
    normalizedOrderType,
    menuCacheVersion,
    catalogueCache,
  ]);

  const safeCatalogue = Array.isArray(catalogueStore) ? catalogueStore : [];

  const activeCategoryData = safeCatalogue.find(
    (cat) => cat.category_name === activeCategory,
  );

  const allItems = safeCatalogue.flatMap((cat) => cat.menu_items || []);

  const findItemByBarcode = (barcode) => {
    const code = String(barcode || "").trim();

    return allItems.find((item) => {
      if (String(item.barcodeNumber || "").trim() === code) return true;
      if (String(item.barcode_number || "").trim() === code) return true;
      if (String(item.barcode || "").trim() === code) return true;
      if (String(item.item_id || "").trim() === code) return true;

      return item.prices?.some(
        (p) =>
          String(p.barcodeNumber || "").trim() === code ||
          String(p.barcode_number || "").trim() === code ||
          String(p.barcode || "").trim() === code,
      );
    });
  };

  const getPriceByBarcode = (item, barcode) => {
    const code = String(barcode || "").trim();

    const matchedPrice = item.prices?.find(
      (p) =>
        String(p.barcodeNumber || "").trim() === code ||
        String(p.barcode_number || "").trim() === code ||
        String(p.barcode || "").trim() === code,
    );

    return matchedPrice || item.prices?.[0];
  };

  const handleBarcodeSubmit = () => {
    const code = barcodeInput.trim();

    if (!code) return;

    const item = findItemByBarcode(code);

    if (!item) {
      toast.error("Item not found");
      setBarcodeInput("");
      return;
    }

    const priceObj = getPriceByBarcode(item, code);

    addToCart(item, priceObj);
    setBarcodeInput("");
  };

  const categories = Array.isArray(catalogueStore)
    ? catalogueStore.map((cat) => cat.category_name)
    : [];

  const getItemKey = (item) => {
    return `${item.item_id}-${item.selectedPrice?.size_id || item.size_id || item.item_id}`;
  };

  const getValidCart = () => {
    return cart.filter((item) => Number(item.qty || item.quantity || 0) > 0);
  };

  const selectedCartItem =
    selectedCartIndex !== null ? cart[selectedCartIndex] : null;

  const hasSelectedCartItem =
    selectedCartIndex !== null &&
    selectedCartIndex >= 0 &&
    selectedCartIndex < cart.length &&
    Number(selectedCartItem?.qty || 0) > 0;

  const handleHold = async () => {
    const validCart = getValidCart();

    if (validCart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }

    const payload = {
      userid: userID,
      currency: "INR",
      custMobile: customerMobile || "919999999999",
      custName: customerName || "",
      orderId: orderId,
      orderType: payloadOrderType,
      tableNo: getCurrentTableNo(),
      roomNumber: isRoomOrder ? getCurrentTableNo() : "",
      paymentMode: "unpaid",
      orderStatus: "hold",
      items: validCart.map((item) => ({
        productId: String(item.item_id),

        sizeId: item.selectedPrice?.size_id || String(item.item_id), // fallback FIX

        productName: item.item_name,
        actualName: item.item_name,

        sizeWords: item.selectedPrice?.size || "",

        itemPrice: String(item.price || item.selectedPrice?.price || 0),

        quantity: String(item.qty),

        customization: JSON.stringify(item.customizations || []),

        hasVarient: item.selectedPrice?.size_id ? "true" : "false",

        itemImage: item.image || "",
      })),
    };

    try {
      const result = await placePosOrder(payload);

      if (result?.responseCode === "00") {
        toast.success("Order put on hold");
        await refreshDiningCache();
        setCart([]);

        await onHoldSuccess?.();

        onClose?.();
      }
    } catch (e) {
      console.error("Hold failed:", e);
      toast.error("Hold failed");
    }
  };

  const handlePaymentClick = async () => {
  try {
    const validCart = getValidCart();

    if (validCart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }

    let finalOrderId = activeOrderId || orderId;

    if (!finalOrderId) {
      const id = await generateOrderID({
        mobile: customerMobile || "9999999999",
        orderType: payloadOrderType,
      });

      if (!id) {
        toast.error("Order ID generation failed");
        return;
      }

      finalOrderId = id;
    setActiveOrderId(id);
setKotOrderId(id);
    }

   const payload = {
  userid: userID,
  currency: "INR",
  custMobile: customerMobile || "919999999999",
  custName: customerName || "Default Customer",

  orderId: finalOrderId,
  orderType: payloadOrderType,
  orderStatus: "hold",

  paymentMode: "unpaid",
  paymentType: "",

  tableNo: getCurrentTableNo(),
  roomNumber: isRoomOrder ? getCurrentTableNo() : "",

  deliveryAmount: "0",
  deliveryChargesDeductedFrom: "customer",
  deliveryPartner: "",

  items: validCart.map((item) => ({
    productId: String(item.item_id),

    sizeId: String(
      item.selectedPrice?.size_id ||
        item.size_id ||
        item.item_id,
    ),

    productName: item.item_name,
    actualName: item.item_name,

    sizeWords:
      item.selectedPrice?.size ||
      item.size ||
      item.sizeWords ||
      "",

    itemPrice: String(
      item.price ||
        item.selectedPrice?.price ||
        0,
    ),

    quantity: String(item.qty || 1),

    customization: JSON.stringify(
      item.customizations || [],
    ),

    hasVarient: item.selectedPrice?.size_id
      ? "true"
      : "false",

    itemImage:
      item.image ||
      item.photo ||
      "",
  })),
};

    const res = await placePosOrder(payload);

    if (res?.responseCode !== "00") {
      toast.error(
        res?.responseMessage ||
          "Order create failed",
      );
      return;
    }

    setActiveOrderId(finalOrderId);
    setKotOrderId(finalOrderId);
    setShowPayment(true);
  } catch (error) {
    console.error("Payment click error:", error);
    toast.error("Something went wrong");
  }
};

  const handleKotClick = async () => {
    const validCart = getValidCart();

    if (validCart.length === 0) {
      toast.warn("Cart is empty");
      return;
    }

    const cartSnapshot = validCart.map((item, index) => {
      const qty = Number(item?.qty || item?.quantity || 1);

      const unitPrice = Number(
        item?.price ||
          item?.item_price ||
          item?.selectedPrice?.price ||
          item?.prices?.[0]?.price ||
          0,
      );

      const itemName =
        item?.item_name ||
        item?.itemName ||
        item?.productName ||
        item?.actualName ||
        item?.name ||
        `Item ${index + 1}`;

      let customizations = [];

      if (Array.isArray(item?.customizations)) {
        customizations = item.customizations;
      } else if (Array.isArray(item?.customization)) {
        customizations = item.customization;
      } else if (
        typeof item?.customization === "string" &&
        item.customization.trim()
      ) {
        try {
          const parsed = JSON.parse(item.customization);

          customizations = Array.isArray(parsed)
            ? parsed
            : parsed
              ? [parsed]
              : [];
        } catch (error) {
          console.error("KOT customization parse error:", error);
          customizations = [];
        }
      }

      return {
        ...item,

        id:
          item?.id ||
          item?.item_id ||
          item?.productId ||
          item?.product_id ||
          `${index}`,

        item_id:
          item?.item_id ||
          item?.itemId ||
          item?.productId ||
          item?.product_id ||
          `${index}`,

        productId:
          item?.productId || item?.product_id || item?.item_id || `${index}`,

        item_name: itemName,
        itemName,
        productName: itemName,
        actualName: itemName,

        quantity: qty,
        qty,

        item_price: unitPrice,
        itemPrice: unitPrice,
        price: unitPrice,

        total_price: unitPrice * qty,
        totalPrice: unitPrice * qty,

        customization: JSON.stringify(customizations),
        customizations,

        selectedPrice: {
          ...(item?.selectedPrice || {}),
          size_id:
            item?.selectedPrice?.size_id ||
            item?.size_id ||
            item?.item_id ||
            "",
          price: unitPrice,
          size:
            item?.selectedPrice?.size || item?.sizeWords || item?.size || "",
        },
      };
    });

    const snapshotSubtotal = cartSnapshot.reduce((sum, item) => {
      return sum + Number(item.total_price || 0);
    }, 0);

    // API calls se pehle snapshot store karo
    setKotCartSnapshot(cartSnapshot);

    try {
      setKotLoading(true);

     let finalOrderId =
  activeOrderId ||
  orderId ||
  kotOrderId;

      if (!finalOrderId) {
        const id = await generateOrderID({
          mobile: customerMobile || "9999999999",
          orderType: payloadOrderType,
        });

        if (!id) {
          toast.error("Order ID generation failed");
          return;
        }

        finalOrderId = id;
      }

      setKotOrderId(finalOrderId);

      const payload = {
        userid: userID,
        currency: "INR",
        custMobile: customerMobile || "919999999999",
        custName: customerName || "Default Customer",
        orderId: finalOrderId,
        orderType: payloadOrderType,
        orderStatus: "hold",
        paymentMode: "unpaid",
        tableNo: getCurrentTableNo(),
        roomNumber: isRoomOrder ? getCurrentTableNo() : "",

        deliveryAmount: "0",
        deliveryChargesDeductedFrom: "customer",
        deliveryPartner: "",

        items: cartSnapshot.map((item) => ({
          productId: String(item.item_id),
          sizeId: String(
            item.selectedPrice?.size_id || item.size_id || item.item_id,
          ),
          productName: item.item_name,
          actualName: item.item_name,
          sizeWords:
            item.selectedPrice?.size || item.sizeWords || item.size || "",
          itemPrice: String(item.item_price),
          quantity: String(item.quantity),
          customization: item.customization,
          hasVarient: item.selectedPrice?.size_id ? "true" : "false",
          itemImage: item.image || item.photo || "",
        })),
      };

      console.log("KOT CART SNAPSHOT:", cartSnapshot);
      console.log("KOT PLACE PAYLOAD:", payload);

      const placeRes = await placePosOrder(payload);

      console.log("KOT PLACE RESPONSE:", placeRes);

      if (placeRes?.responseCode !== "00") {
        toast.error(placeRes?.responseMessage || "KOT order create failed");
        return;
      }

      // Modal ko API detail response par depend nahi rakhenge
      const fallbackKotOrder = {
        orderid: finalOrderId,
        orderId: finalOrderId,

        customer_mobile: customerMobile || "9999999999",

        custMobile: customerMobile || "9999999999",

        customer_name: customerName || "Default Customer",

        orderType: payloadOrderType,

        table_no: getCurrentTableNo(),
        tableNo: getCurrentTableNo(),

        roomNumber: isRoomOrder ? getCurrentTableNo() : "",

        items: cartSnapshot,
        orderItems: cartSnapshot,
        order_items: cartSnapshot,
        cart: cartSnapshot,

        subtotal: snapshotSubtotal,
        discount_amount: 0,
        totalOrderPrice: snapshotSubtotal,
        order_amount: snapshotSubtotal,
      };

      // Pehle guaranteed cart data ke saath modal open karo
      setKotOrderData(fallbackKotOrder);
      setShowKotModal(true);

      // Print API failure se modal affect nahi hoga
      try {
        await printKot(finalOrderId);
      } catch (printError) {
        console.error("Print KOT API error:", printError);
      }

      // API details available hain to basic information merge karo,
      // lekin items hamesha snapshot se rakho
      try {
        const details = await getOrderDetails(finalOrderId);

        console.log("KOT DETAILS RESPONSE:", details);

        let fullOrder = null;

        if (Array.isArray(details)) {
          fullOrder = details[0] || null;
        } else if (Array.isArray(details?.response)) {
          fullOrder = details.response[0] || null;
        } else if (details?.response && typeof details.response === "object") {
          fullOrder = details.response;
        } else if (details && typeof details === "object") {
          fullOrder = details;
        }

        if (fullOrder) {
          setKotOrderData((previous) => ({
            ...(previous || fallbackKotOrder),
            ...fullOrder,

            orderid: fullOrder?.orderid || fullOrder?.orderId || finalOrderId,

            orderId: fullOrder?.orderId || fullOrder?.orderid || finalOrderId,

            customer_mobile:
              fullOrder?.customer_mobile ||
              fullOrder?.custMobile ||
              customerMobile ||
              "9999999999",

            orderType:
              fullOrder?.orderType || fullOrder?.order_type || payloadOrderType,

            table_no:
              fullOrder?.table_no || fullOrder?.tableNo || getCurrentTableNo(),

            tableNo:
              fullOrder?.tableNo || fullOrder?.table_no || getCurrentTableNo(),

            // Important: API ke empty items se overwrite nahi karna
            items: cartSnapshot,
            orderItems: cartSnapshot,
            order_items: cartSnapshot,
            cart: cartSnapshot,

            subtotal:
              Number(
                fullOrder?.subtotal || fullOrder?.subTotal || snapshotSubtotal,
              ) || snapshotSubtotal,

            discount_amount:
              Number(
                fullOrder?.discount_amount || fullOrder?.discountAmount || 0,
              ) || 0,

            totalOrderPrice:
              Number(
                fullOrder?.totalOrderPrice ||
                  fullOrder?.order_amount ||
                  snapshotSubtotal,
              ) || snapshotSubtotal,
          }));
        }
      } catch (detailsError) {
        console.error("Get KOT order details error:", detailsError);
      }
    } catch (err) {
      console.error("KOT flow failed:", err);
      toast.error("KOT generation failed");
    } finally {
      setKotLoading(false);
    }
  };

  const applyCustomization = () => {
    if (!hasSelectedCartItem) {
      toast.warn("Selected item not found");
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
      return;
    }

    const updatedCart = [...cart];
    const currentItem = updatedCart[selectedCartIndex];

    if (!currentItem || Number(currentItem.qty || 0) <= 0) {
      toast.warn("Selected item not found");
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
      return;
    }

    const customizations = [];

    if (selectedOptions.type) {
      customizations.push({
        customization_type: "customize",
        customize_field: "type",
        options: [selectedOptions.type],
      });
    }

    if (selectedOptions.addons?.length > 0) {
      customizations.push({
        customization_type: "customize",
        customize_field: "addons",
        options: selectedOptions.addons,
      });
    }

    updatedCart[selectedCartIndex] = {
      ...currentItem,
      customizations,
    };

    setCart(updatedCart);
    setSelectedOptions({});
    setShowCustomize(false);
  };

  const items = activeCategoryData?.menu_items || [];
  const safeItems = Array.isArray(items) ? items : [];
  const safeArray = (arr) => (Array.isArray(arr) ? arr : []);

  const filteredItems = useMemo(() => {
    if (!searchItem) return safeItems;
    return safeItems.filter((i) =>
      i.item_name.toLowerCase().includes(searchItem.toLowerCase()),
    );
  }, [safeItems, searchItem]);

  const addToCart = (item, selectedPrice) => {
    const priceObj = selectedPrice || item.prices?.[0];
    const itemKey = `${item.item_id}-${priceObj.size_id}`;

    setCart((prev) => {
      const existing = prev.find(
        (c) => `${c.item_id}-${c.selectedPrice?.size_id}` === itemKey,
      );

      if (existing) {
        return prev.map((c) =>
          `${c.item_id}-${c.selectedPrice?.size_id}` === itemKey
            ? { ...c, qty: c.qty + 1 }
            : c,
        );
      }

      return [
        ...prev,
        {
          ...item,
          qty: 1,
          selectedPrice: priceObj,
          price: Number(priceObj.price),
        },
      ];
    });
  };

  const removeFromCart = (item) => {
    const itemKey = getItemKey(item);

    setCart((prev) => {
      const existingIndex = prev.findIndex((c) => getItemKey(c) === itemKey);

      if (existingIndex === -1) return prev;

      const existing = prev[existingIndex];

      // qty 1 hai to item remove hoga, 0 nahi banega
      if (Number(existing.qty) <= 1) {
        const updated = prev.filter((c) => getItemKey(c) !== itemKey);

        // selected item remove ho gaya to selection clear
        if (selectedCartIndex === existingIndex) {
          setSelectedCartIndex(null);
          setSelectedOptions({});
          setShowCustomize(false);
        }

        // agar removed index ke baad selection shift karni ho
        if (selectedCartIndex !== null && selectedCartIndex > existingIndex) {
          setSelectedCartIndex(selectedCartIndex - 1);
        }

        return updated;
      }

      return prev.map((c) =>
        getItemKey(c) === itemKey
          ? { ...c, qty: Math.max(Number(c.qty || 1) - 1, 1) }
          : c,
      );
    });
  };

  const clearCart = () => {
    setCart([]);
    setSelectedCartIndex(null);
    setSelectedOptions({});
    setShowCustomize(false);
  };

  const toNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const getOrgTaxStorageKey = (userid) => `pos_org_tax_config_${userid}`;

  const getStoredTaxConfig = () => {
    try {
      const raw = sessionStorage.getItem(getOrgTaxStorageKey(userID));
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.error("DineScreen tax config parse error:", error);
      return {};
    }
  };

  const cartSubtotal = cart.reduce((sum, c) => {
    return sum + Number(c.price || 0) * Number(c.qty || 0);
  }, 0);

  const storedTaxConfig = getStoredTaxConfig();

  const gstType =
    storedTaxConfig?.gst_type || storedTaxConfig?.gstType || "excl";

  const normalizedGstType = String(gstType || "")
    .toLowerCase()
    .trim();

  const isInclusiveGst =
    normalizedGstType === "incl" || normalizedGstType === "inclusive";

  const roundAmount = (value) => {
    const num = Number(value || 0);

    if (!Number.isFinite(num)) return 0;

    return Math.ceil(num);
  };

  // Hardcoded 5% GST included formula.
  // Same concept used in other files:
  // Total GST = amount - amount / 1.05
  // CGST = Total GST / 2
  // SGST = Total GST / 2
  const getHardcodedTaxAmounts = (amount) => {
    const taxableAmount = Math.max(Number(amount || 0), 0);

    const includedTotalTax = taxableAmount - taxableAmount / 1.05;

    return {
      cgst: includedTotalTax / 2,
      sgst: includedTotalTax / 2,
    };
  };

  const dineTaxAmounts = getHardcodedTaxAmounts(cartSubtotal);

  const dineCgstAmount = dineTaxAmounts.cgst;
  const dineSgstAmount = dineTaxAmounts.sgst;

  const calculatedTotal = isInclusiveGst
    ? cartSubtotal
    : cartSubtotal + dineCgstAmount + dineSgstAmount;

  const total = roundAmount(calculatedTotal);

  useEffect(() => {
    if (!barcodeInput.trim()) return;

    const timer = setTimeout(() => {
      handleBarcodeSubmit();
    }, 350);

    return () => clearTimeout(timer);
  }, [barcodeInput]);

  useEffect(() => {
    if (selectedCartIndex !== null && selectedCartIndex >= cart.length) {
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
    }

    if (cart.length === 0) {
      setSelectedCartIndex(null);
      setSelectedOptions({});
      setShowCustomize(false);
    }
  }, [cart, selectedCartIndex]);

  useEffect(() => {
  setActiveOrderId(orderId || "");
}, [orderId]);

  useEffect(() => {
    if (Array.isArray(existingItems) && existingItems.length > 0) {
      const preloaded = existingItems.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        qty: Number(item.quantity || item.qty || 1),
        price: Number(item.item_price || item.price || 0),

        selectedPrice: {
          size_id: item.size_id || item.item_id,
          price: item.item_price || item.price || 0,
          size: item.sizeWords || item.size || "",
        },

        size: item.sizeWords || "Full Plate",
        image: item.image || item.itemImage || "",
        customizations: (() => {
          if (Array.isArray(item.customizations)) return item.customizations;

          try {
            return item.customization ? JSON.parse(item.customization) : [];
          } catch {
            return [];
          }
        })(),
      }));

      setCart(preloaded);
    }
  }, [existingItems]);

  if (showPayment) {
  return (
    <div className="h-full w-full bg-gray-100">
      <PaymentModal
        cart={getValidCart()}
        total={cartSubtotal}
        orderId={activeOrderId || orderId}
        reciptDetails={reciptDetails}
        customerMobile={customerMobile}
        orderType={payloadOrderType}

        selectedTable={{
          tableNo: getCurrentTableNo(),
          table_no: getCurrentTableNo(),

          roomNumber: isRoomOrder
            ? getCurrentTableNo()
            : "",

          roomNo: isRoomOrder
            ? getCurrentTableNo()
            : "",

          room_no: isRoomOrder
            ? getCurrentTableNo()
            : "",
        }}

        selectedRoom={
          isRoomOrder
            ? {
                roomNumber: getCurrentTableNo(),
                roomNo: getCurrentTableNo(),
                room_no: getCurrentTableNo(),
              }
            : null
        }

        onAfterOrderSuccess={async () => {
          setCart([]);
          setActiveOrderId("");

          await onHoldSuccess?.();
        }}

        onClose={(action) => {
          setShowPayment(false);

          if (action === "home") {
            setCart([]);
            setActiveOrderId("");
            onClose?.();
          }
        }}
      />
    </div>
  );
}

  return (
    <>
    <ToastContainer/>
      <div className="flex h-full font-sans text-sm select-none montserrat-font-1 overflow-hidden">
        {/*  LEFT COLUMN — Cart + Payment button */}
        <div className="w-80 flex-shrink-0 bg-white flex flex-col border-r border-gray-200">
          {/* Barcode input */}
          <div className="px-2 py-2 border-b border-gray-200">
            <input
              ref={searchRef}
              type="text"
              placeholder="Scan or search by barcode / item ID"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBarcodeSubmit();
                }
              }}
              className="w-full border border-blue-400 rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300 placeholder-gray-400"
            />
          </div>

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                Cart is empty
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                  <tr className="text-gray-500">
                    <th className="text-left px-2 py-2 font-medium">Item</th>
                    <th className="text-center px-2 py-2 font-medium">Qty</th>
                    <th className="text-right px-3 py-2 font-medium">Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {safeArray(cart).map((item, index) => (
                    <tr
                      key={`${item.item_id}-${item.selectedPrice?.size_id}`}
                      onClick={() => setSelectedCartIndex(index)}
                      className={`border-b border-gray-100 cursor-pointer ${
                        selectedCartIndex === index ? "bg-blue-100" : ""
                      }`}
                    >
                      {/* ITEM NAME */}
                      <td className="px-2 py-2 font-medium text-gray-700 text-[14px] leading-tight">
                        {item.item_name}

                        {item.customizations?.length > 0 && (
                          <div className="text-[12px] text-gray-600 mt-1">
                            {item.customizations
                              .flatMap((c) => c.options)
                              .join(", ")}
                          </div>
                        )}
                      </td>

                      {/* QTY CONTROLS */}
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromCart(item);
                            }}
                            className="w-6 h-6 rounded bg-red-100 text-red-600 font-bold hover:bg-red-200 flex items-center justify-center"
                          >
                            −
                          </button>

                          <span className="w-6 text-center font-semibold text-gray-800">
                            {item.qty}
                          </span>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(item, item.selectedPrice);
                            }}
                            className="w-6 h-6 rounded bg-green-100 text-green-600 font-bold hover:bg-green-200 flex items-center justify-center"
                          >
                            +
                          </button>
                        </div>
                      </td>

                      {/* AMOUNT */}
                      <td className="px-3 py-2 text-right font-semibold text-gray-800">
                        ₹{(item.price * item.qty).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Total row */}
          <div className="border-t border-gray-200 px-4 py-2 flex justify-between items-center bg-gray-50">
            <span className="font-bold text-gray-800 text-base">Total</span>

            <span className="font-bold text-gray-800 text-base">₹ {total}</span>
          </div>

          {/* LEFT bottom: Payment only */}
          <button
            onClick={handlePaymentClick}
            className="bg-emerald-500 hover:bg-emerald-900 active:bg-gray-800 text-white font-bold mb-2 p-3.5 mx-4 rounded-lg text-sm transition-colors tracking-wide border-t border-gray-200 flex-shrink-0"
          >
            Payment
          </button>
        </div>

        {/* CENTER COLUMN */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Items panel */}
          <div className="flex-1 flex flex-col bg-white mx-2 mt-2 rounded-t-lg shadow-sm overflow-hidden relative">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-800">
                {activeCategory}
              </h2>
              <input
                type="text"
                placeholder="Search item..."
                value={searchItem}
                onChange={(e) => setSearchItem(e.target.value)}
                className="border border-blue-400 rounded px-3 py-2.5 text-xs w-60 outline-none focus:ring-2 focus:ring-blue-300 placeholder-gray-400"
              />
            </div>

            {/* Items grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {catalogLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Loading items...</span>
                  </div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                  No items found
                </div>
              ) : (
                <div className="grid grid-cols-6 gap-2">
                  {filteredItems.map((item) => {
                    const inCart = cart.find((c) => c.item_id === item.item_id);
                    return (
                      <button
                        key={item.item_id}
                        onClick={() => {
                          if (!item.prices || item.prices.length === 1) {
                            addToCart(item, item.prices[0]);
                          }
                        }}
                        className={`relative px-2 py-3 rounded border text-xs font-medium text-center transition-all hover:shadow-sm active:scale-95 ${
                          inCart
                            ? "border-emerald-400 text-emerald-700"
                            : "border-gray-800 bg-blue-100 text-gray-700 hover:border-blue-400 hover:bg-blue-300"
                        }`}
                      >
                        <div className="leading-tight block">
                          <div className="text-[13px] font-medium text-gray-800">
                            {item.item_name}
                          </div>

                          {/*  Variant / Price */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {item.prices?.length > 1 ? (
                              item.prices.map((p) => (
                                <button
                                  key={`${item.item_id}-${p.size_id || p.price}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(item, p);
                                  }}
                                  className="px-3 py-2 text-[14px] font-semibold border border-blue-300 bg-gray-100 text-blue-800 rounded-lg hover:bg-blue-200 transition shadow-sm"
                                >
                                  {p.price}
                                </button>
                              ))
                            ) : (
                              <span className="px-3 py-2 text-[14px] font-semibold border border-gray-300 bg-gray-100 text-gray-700 rounded-md">
                                ₹{item.prices?.[0]?.price}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Cart badge */}
                        {inCart && (
                          <span className="absolute -top-1.5 -right-1.5 bg-emerald-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold shadow">
                            {inCart.qty}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Watermark */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <span className="text-8xl font-black text-gray-400 select-none tracking-widest opacity-20">
                Anlook POS
              </span>
            </div>
          </div>

          {/* CENTER bottom: 5 buttons in one row */}
          <div className="flex shadow-sm rounded-lg overflow-hidden flex-shrink-0 gap-3 mb-2 mx-3 mt-2">
            <button
              onClick={() => setShowCustomerModal(true)}
              className="flex-1 bg-gray-600 hover:bg-gray-700 active:bg-gray-800 text-white font-bold py-4 text-xs transition-colors tracking-wide"
            >
              Customer
            </button>
            <button
              onClick={clearCart}
              className="flex-1 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (!hasSelectedCartItem) {
                  toast.warn("Select item first");
                  setSelectedCartIndex(null);
                  setSelectedOptions({});
                  return;
                }

                const itemCustomizations =
                  selectedCartItem?.customizations || [];

                const typeCustomization = itemCustomizations.find(
                  (c) => c.customize_field === "type",
                );

                const addonCustomization = itemCustomizations.find(
                  (c) => c.customize_field === "addons",
                );

                setSelectedOptions({
                  type: typeCustomization?.options?.[0] || "",
                  addons: addonCustomization?.options || [],
                });

                setShowCustomize(true);
              }}
              className="flex-1 bg-cyan-400 hover:bg-cyan-500 active:bg-cyan-600 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Customize
            </button>
            <button
              onClick={handleHold}
              className="flex-1 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-600 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              Hold
            </button>

            <button
              onClick={handleKotClick}
              disabled={kotLoading}
              className="flex-1 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-4 text-xs transition-colors tracking-wide border-l border-white/30"
            >
              {kotLoading ? "Generating..." : "KOT"}
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════
          RIGHT COLUMN — Categories + Cancel btn
      ═══════════════════════════════════════ */}
        <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col">
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-700 text-center">
              Category
            </h3>
          </div>

          {/* Category list */}
          <div className="flex-1 overflow-y-auto py-2 px-2">
            {catalogLoading ? (
              <div className="flex justify-center pt-6">
                <div className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {safeArray(categories).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setSearchItem("");
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium rounded-md transition-colors border ${
                      activeCategory === cat
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "text-gray-700 hover:bg-gray-100 border-gray-200 bg-white"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT bottom: Cancel only */}
         <button
  type="button"
  onClick={handleCancel}
  className="bg-gray-600 hover:bg-black active:bg-gray-800 text-white font-bold mb-2 p-3.5 mx-4 rounded-lg text-sm transition-colors tracking-wide border-t border-gray-200 flex-shrink-0"
>
  Cancel
</button>
        </div>

        {/* mobile number model */}
        <MobileNumberModal
          open={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          orderType={orderType}
          initialMobile={customerMobile}
          initialName={customerName}
          onSubmit={(data) => {
            setCustomerMobile(normalizeMobile(data.mobile));
            setCustomerName(data.name);
          }}
        />

        <KotGeneratedModal
          open={showKotModal}
          order={kotOrderData}
          cart={kotCartSnapshot}
          orderId={kotOrderId || orderId}
          customerMobile={customerMobile}
          orderType={payloadOrderType}
          onClose={() => {
            setShowKotModal(false);
            setKotOrderData(null);
            setKotCartSnapshot([]);
            onClose?.();
          }}
          onDirectPrint={async () => {
            const finalPrintOrderId = kotOrderId || orderId;

            if (!finalPrintOrderId) {
              toast.error("Order ID not found");
              return;
            }

            await printKot(finalPrintOrderId);
          }}
        />

        {/* customization model */}

        {showCustomize && hasSelectedCartItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="flex w-[800px] bg-white rounded-xl overflow-hidden shadow-xl">
              {/* LEFT SIDE */}
              <div className="flex-1 bg-yellow-50 p-6">
                <h2 className="text-xl font-bold text-yellow-600 text-center">
                  Customize
                </h2>

                <p className="text-center text-gray-600 mb-4">
                  {selectedCartItem?.item_name}
                </p>

                {/* Choose One */}
                <p className="font-semibold mb-2">Choose One:</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["Spicy", "Non Spicy", "Gravy", "Dry", "No garlic"].map(
                    (opt) => (
                      <button
                        key={opt}
                        onClick={() =>
                          setSelectedOptions((s) => ({
                            ...s,
                            type: opt,
                          }))
                        }
                        className={`px-4 py-2 rounded border text-sm ${
                          selectedOptions.type === opt
                            ? "bg-yellow-400 text-white"
                            : "bg-white"
                        }`}
                      >
                        {opt}
                      </button>
                    ),
                  )}
                </div>

                {/* Add-ons */}
                <p className="font-semibold mb-2">Add-ons:</p>
                <div className="flex flex-wrap gap-2">
                  {["Onion", "Garlic", "Without Garlic"].map((opt) => {
                    const selected = selectedOptions.addons || [];

                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          const updated = selected.includes(opt)
                            ? selected.filter((v) => v !== opt)
                            : [...selected, opt];

                          setSelectedOptions((s) => ({
                            ...s,
                            addons: updated,
                          }));
                        }}
                        className={`px-4 py-2 rounded border text-sm ${
                          selected.includes(opt)
                            ? "bg-blue-500 text-white"
                            : "bg-white"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT SIDE */}
              <div className="w-64 bg-gray-100 flex flex-col justify-center items-center gap-4 p-6">
                <button
                  onClick={applyCustomization}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 rounded-lg shadow"
                >
                  APPLY & CLOSE
                </button>

                <button
                  onClick={() => setShowCustomize(false)}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-black font-bold py-4 rounded-lg shadow"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
